import React, { useContext, useEffect, useState } from 'react'
import { Wrapper, ModalBody, FooterActions } from './DropdownStyles'

import { DashboardContext } from 'live/context/dashboard'
import LiveModal from 'live/components/ui/Modal'
import i18n from 'live/services/i18n'

import Tree from '../antd Components/tree'
import { TreeHeader } from './DropdownHeader'
import { updateSpanAndIdentity } from '../utils/RefreshFunctions'
import { refreshFunctions, SpanPickerContext } from '../utils/initialStates'
import TimespanSelect from './timespanSelect'
import styled from 'styled-components'
import { parseOptions } from '../utils/utils'

/**
 * This component connects and calls the header and the tree of the dropdown.
 * @param tree {TreeComponentProps} - data related to the tree (the items on the tree, an array of keys to all the items and the default expanded nodes)
 * @param select {TreeSelectingProps} - data related to the selection of the tree (the item selected, the function to change its selection, the timespan of the selected item and the run reference of the selected item)
 * @param check {TreeCheckingProps} - data related to the check items of the tree (data of the mainCheck, items checked, quantity of checkedOperations, and an option of using the individual timespans or the min and max of them)
 * @param selectFilter {selectFilterProps} - an object that contains the data related to a dropdown used to filter a tree by its operations.
 * @param textFilter {textFilterProps} - an object that contains data related to a text input that filters the tree.
 * @returns {JSX.Element} - a child that will return a JSX Element
 */

const TreeDropdown = ({
    select,
    check,
    tree,
    selectFilter,
    textFilter
}: {
    tree: TreeFilteredComponentProps
    select: TreeSelectingProps
    check: TreeCheckingProps
    selectFilter: selectFilterProps
    textFilter: textFilterProps
}) => {
    const { state, params, ...context } = useContext(DashboardContext)
    const [filteredTree, setFilteredTree] = useState(tree.filteredTree)
    const { operationsPickerConfigs, headerContext, changeSpanPickerConfig } = useContext(SpanPickerContext)

    useEffect(() => setFilteredTree(tree.filteredTree), [JSON.stringify(tree.filteredTree)])

    const onRefresh = () => {
        changeSpanPickerConfig({
            ...operationsPickerConfigs,
            advancedMode: false,
        })
        if (select.selectedKey.length === 1) {
            updateSpanAndIdentity(
                operationsPickerConfigs,
                context,
                tree.formattedTree,
                select.selectedKey,
                select.selectedTimespan,
                select.selectedRunReference,
                check.checkedKeys,
                headerContext,
                refreshFunctions
            )
            headerContext.toggleOpen()
        }
    }

    return (
        <CustomModal className={`modal  ${headerContext.advancedMode ? 'modal-sm' : 'modal-lg'}`}  show={headerContext.open}>
            {!headerContext.advancedMode ? (
                <>
                    <LiveModal.Header>
                        <LiveModal.Title>{i18n('Select Drilling Run or Operation')}</LiveModal.Title>
                    </LiveModal.Header>
                    <LiveModal.Body>
                        <ModalBody>
                            <Wrapper>
                                <TreeHeader
                                    tree={tree}
                                    selectFilter={selectFilter}
                                    check={check}
                                    textFilter={textFilter}
                                />
                                <div className="tree-component">
                                    <Tree
                                        checkable={operationsPickerConfigs.enableChecking}
                                        treeData={filteredTree}
                                        defaultExpandedKeys={tree.defaultExpandedKeys}
                                        onSelect={e => select.onSelect(e)}
                                        onCheck={e => check.onCheck(e)}
                                        checkedKeys={check.checkedKeys}
                                        selectedKeys={select.selectedKey}
                                    />
                                </div>
                            </Wrapper>
                        </ModalBody>
                    </LiveModal.Body>
                    <LiveModal.Footer>
                        <FooterActions>
                            <button
                                className="btn btn-primary btn-bold"
                                disabled={select?.selectedKey?.length === 0}
                                onClick={() => onRefresh()}
                            >
                                Refresh
                            </button>
                            <span>{i18n('or')}</span>
                            <a onClick={() => headerContext.toggleOpen()}>{i18n('Cancel')}</a>
                            <a
                                className="mode-selector"
                                onClick={() => headerContext.setAdvancedMode(!headerContext.advancedMode)}
                            >
                                {i18n('Advanced')}
                            </a>
                        </FooterActions>
                    </LiveModal.Footer>
                </>
            ) : (
                <TimespanSelect />
            )}
        </CustomModal>
    )
}

export default TreeDropdown

const CustomModal = styled(LiveModal)`
        .modal-dialog {
            height: calc(${({height}) => height || '100%'} - 40px);
            margin: 20px auto !important;
            
            .modal-content {
                height: 100%;

                .modal-body { 
                    height: calc(100% - 109px);
                }
            }
        }
`